import {
  Alert,
  Button,
  Card,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { CreateJobsRequest } from "protos/rtc/jobs";
import { DryRunPreview } from "../DryRunPreview";
import {
  formStateToCreateJobRequest,
  jobFieldIds,
  NewJobsFormState,
} from "../FormState";
import { NewJobsWizardFormProps } from "../NewJobsWizard";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { WizardStepButtonBar } from "../WizardStepButtonBar";
import PreviewPathIcon from "portal/images/icons/conversion_path.svg?react";
import React, { useCallback, useMemo } from "react";

type InSeasonConfigurePreviewFormProps = Pick<
  NewJobsWizardFormProps,
  "customerId" | "farmMapData" | "farmIdByFieldId"
>;

export const InSeasonConfigurePreviewForm: React.FC<
  InSeasonConfigurePreviewFormProps
> = ({ customerId, farmMapData, farmIdByFieldId }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<NewJobsFormState>();
  const [doDryRun, dryRunResult] = rtcJobsApi.useCreateBulkJobsMutation({});

  // in-season jobs work one field along one planting path, so there's only
  // ever a single dry run to look at
  const dryRunJob = dryRunResult.data?.jobs[0];
  const dryRunFarm = useMemo(() => {
    return dryRunJob?.farmId ? farmMapData[dryRunJob.farmId] : undefined;
  }, [farmMapData, dryRunJob?.farmId]);
  const dryRunField = useMemo(() => {
    return dryRunFarm?.zonesById[dryRunJob?.fieldId ?? ""];
  }, [dryRunFarm?.zonesById, dryRunJob?.fieldId]);

  const onClickPreview = useCallback(() => {
    const fieldId = jobFieldIds(values, farmMapData, farmIdByFieldId)[0];
    const farmId = fieldId ? farmIdByFieldId[fieldId] : undefined;
    const farm = farmId ? farmMapData[farmId] : undefined;

    if (!fieldId || !farm) {
      throw new Error(
        `InSeasonConfigurePreviewForm.onClickPreview: failed to get map data for ${fieldId}`
      );
    }

    const bulkJobReq: CreateJobsRequest = {
      jobRequests: formStateToCreateJobRequest(
        farm,
        // not localized because this string is only visible to carbon users
        () => `create jobs preview ${Date.now()}`,
        customerId,
        fieldId,
        true, // use dry runs for preview
        values,
        {}
      ),
    };

    doDryRun(bulkJobReq);
  }, [customerId, doDryRun, farmIdByFieldId, farmMapData, values]);

  let previewSection: JSX.Element | undefined;

  if (dryRunResult.isError) {
    // network request failed
    previewSection = (
      <Alert severity="warning" className="w-full text-base rounded-lg">
        {t(
          "views.autotractor.jobCreation.step3ConfigurePreview.networkFailureAlert"
        )}
      </Alert>
    );
  } else if (dryRunResult.isSuccess && dryRunJob) {
    // network request succeeded; we can display a preview (even if creating the
    // job itself failed)
    previewSection = (
      <>
        <Typography>
          {t("views.autotractor.jobCreation.step3ConfigurePreview.pathPreview")}
        </Typography>
        <Card className="rounded-lg p-4 w-full flex flex-row items-center justify-between">
          <div className="flex flex-col">
            <Typography>{dryRunField?.name ?? ""}</Typography>
            <Typography variant="body2">{dryRunFarm?.name ?? ""}</Typography>
          </div>
        </Card>
        <DryRunPreview
          dryRun={dryRunJob}
          showJobData={"objective"}
          farmMapData={dryRunFarm}
          configValueDisplayModules={[]}
        />
      </>
    );
  }

  return (
    <>
      <WizardStepButtonBar disableNext={false} />
      <Paper
        variant="outlined"
        className="bg-inherit p-4 flex flex-col gap-4 rounded-lg"
      >
        <Typography variant="h5">
          {t("views.autotractor.jobCreation.step3ConfigurePreview.title")}
        </Typography>
        <Typography color="textSecondary">
          {t(
            "views.autotractor.jobCreation.step3ConfigurePreview.inSeason.description"
          )}
        </Typography>
        <hr className="w-full" />
        <div className="flex flex-row items-center gap-4">
          {dryRunResult.isLoading ? (
            <Button
              variant="contained"
              startIcon={
                <CircularProgress variant="indeterminate" className="w-6 h-6" />
              }
              className="w-1/5"
              disabled={true}
            >
              {t(
                "views.autotractor.jobCreation.step3ConfigurePreview.generatingPath"
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<PreviewPathIcon />}
              className="w-1/5"
              onClick={onClickPreview}
            >
              {t(
                "views.autotractor.jobCreation.step3ConfigurePreview.previewPath"
              )}
            </Button>
          )}
        </div>
        {previewSection}
      </Paper>
      <WizardStepButtonBar disableNext={false} />
    </>
  );
};
